import { useReduxState } from 'hooks';
import Router from 'next/router';
import { breakpoints } from 'styles';
import { scrollToElement, highlightDivContents, scrollToTop } from 'lib/common';

export type EditorSearchData = {
  blockId: number;
  chapter: string;
  content: string;
  url: string;
};

interface Props {
  data: EditorSearchData[];
}

const EditorSearchLocal = ({ data }: Props) => {
  const {
    pageState: { course },
    theme
  } = useReduxState();

  const handleResultClick = (
    ev: React.SyntheticEvent<HTMLButtonElement, MouseEvent>
  ) => {
    const blockId = parseInt(
      ev.currentTarget.getAttribute('data-block-id'),
      10
    );
    const dataUrl = ev.currentTarget.getAttribute('data-url').split('/');
    const courseSlug = dataUrl[2];
    const chapterSlug = dataUrl[3];

    const url = `/admin/courses/${courseSlug}/edit/${chapterSlug}`;

    if (window.location.pathname === url) {
      // block with search results in the same chapter
      const element = document.querySelector(
        `.block[data-block-id="${blockId}"]`
      ) as HTMLElement;

      if (blockId === -1) scrollToTop({ duration: 300 });
      else {
        scrollToElement({ element, offset: 200 });
        highlightDivContents({ element });
      }
    } else {
      // block with search result is in another chapter
      sessionStorage.setItem('editorQueryRoutingBlockId', `${blockId}`);
      Router.push(`/admin/courses/[courseSlug]/edit/[chapterSlug]`, url);
    }
  };

  return (
    <div className="local">
      <div className="results">
        {data?.length &&
          data.map((result, i: number) => (
            <button
              key={i}
              className="result"
              data-block-id={result.blockId}
              data-url={result.url}
              lang={course?.language}
              onClick={handleResultClick}
            >
              {!result.content ? (
                <div
                  className="chapter chapter-title"
                  dangerouslySetInnerHTML={{
                    __html: result.chapter
                      .replace(/\n/g, '<br />')
                      .replace(/\t/g, '&emsp;')
                  }}
                />
              ) : (
                <>
                  <div className="chapter">{result.chapter}</div>
                  <div
                    className="text"
                    dangerouslySetInnerHTML={{
                      __html: result.content
                        .replace(/\n/g, '<br />')
                        .replace(/\t/g, '&emsp;')
                    }}
                  />
                </>
              )}
            </button>
          ))}
      </div>

      <style jsx>{`
        .local {
          border-radius: 0 0 5px 5px;
          overflow: auto;
          padding-bottom: 5px;
        }
        .result {
          border: 0;
          color: ${theme.textColorLighter};
          background-color: transparent;
          cursor: pointer;
          display: block;
          font-family: inherit;
          outline: 0;
          padding: 10px 13px;
          text-align: left;
          transition: color 0.3s;
          width: 100%;
          word-break: break-word;
          border-top: 1px solid ${theme.lightGreyDark};
        }
        .result:focus,
        .result:hover {
          color: ${theme.textColor};
        }
        .result:not(:first-child) {
          margin-top: 10px;
        }
        .chapter {
          font-size: 1.3rem;
          font-weight: 600;
          margin-top: 10px;
        }
        .chapter-title {
          font-size: 1.6rem;
        }
        .text {
          font-size: 1.5rem;
          margin-top: 5px;
        }
        @media (max-width: ${breakpoints.medium - 1}px) {
          .local {
            max-height: 300px;
          }
        }
        @media (min-width: ${breakpoints.medium}px) {
          .local {
            background: linear-gradient(white 30%, rgba(255, 255, 255, 0)),
              linear-gradient(rgba(255, 255, 255, 0), white 70%) 0 100%,
              radial-gradient(
                50% 0,
                farthest-side,
                rgba(0, 0, 0, 0.2),
                rgba(0, 0, 0, 0)
              ),
              radial-gradient(
                  50% 100%,
                  farthest-side,
                  rgba(0, 0, 0, 0.2),
                  rgba(0, 0, 0, 0)
                )
                0 100%;
            background: linear-gradient(white 30%, rgba(255, 255, 255, 0)),
              linear-gradient(rgba(255, 255, 255, 0), white 70%) 0 100%,
              radial-gradient(
                farthest-side at 50% 0,
                rgba(0, 0, 0, 0.2),
                rgba(0, 0, 0, 0)
              ),
              radial-gradient(
                  farthest-side at 50% 100%,
                  rgba(0, 0, 0, 0.2),
                  rgba(0, 0, 0, 0)
                )
                0 100%;
            background-attachment: local, local, scroll, scroll;
            background-color: white;
            // background-position-y: 0px, 100%, 1px, 100%;
            background-repeat: no-repeat;
            background-size: 100% 40px, 100% 40px, 100% 14px, 100% 14px;
          }
        }
      `}</style>
    </div>
  );
};

export default EditorSearchLocal;
